import React, { useRef, useState } from 'react';
import _get from 'lodash/get';
import { useSelector } from 'react-redux';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  makeStyles,
  Typography,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import ReplayIcon from '@material-ui/icons/Replay';
import Mixpanel from 'utils/mixpanelService';
import { ResourcePageHelpfulContent } from 'components/ResourcePageHelpful/ResourcePageHelpfulContent';
import { useSaveToFavorite } from 'components/useSaveToFavorite';
import { HeartIcon } from 'containers/Series/components/icons/HeartIcon';
import { m3u8Type, PracticeVideo } from './PracticeVideo';
import { PracticeAudio } from './PracticeAudio';
import PracticeSlides from './PracticeSlidesLoadable';

const practiceTypesMapping = {
  video: 'video',
  slides: 'pdf',
  audio: 'audio',
};

const typesRegex = [
  {
    regex: new RegExp(`${m3u8Type.join('|')}|video`),
    type: practiceTypesMapping.video,
  },
  {
    regex: /audio/,
    type: practiceTypesMapping.audio,
  },
  {
    regex: /pdf/,
    type: practiceTypesMapping.slides,
  },
];

const useStyles = makeStyles(theme => ({
  paper: {
    minHeight: 300,
    maxWidth: 'unset',
    width: 1024,
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    padding: '8px 24px 0px',
  },
  titleContainer: {
    display: 'flex',
  },
  heartButton: {
    marginLeft: theme.spacing(1),
    padding: 6,
    color: props =>
      props.saved
        ? theme.palette.primary.secondary
        : theme.palette.text.primary,
  },
  content: props => ({
    padding: `${theme.spacing(1)}px ${theme.spacing(3)}px ${theme.spacing(
      3,
    )}px`,
    ...(practiceTypesMapping.audio === props.type && {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
    }),
  }),
  closeButton: {
    alignSelf: 'flex-end',
    padding: theme.spacing(1),
    '& svg': {
      fontSize: '1.25rem',
    },
  },
  resourceHelpful: {
    marginTop: theme.spacing(3),
  },
  resourceHelpfulBtnWrapper: {
    justifyContent: 'flex-start',
    '& button + button': {
      marginLeft: theme.spacing(4),
    },
  },
  startAgainButton: {
    fontWeight: theme.typography.fontWeightRegular,
    fontSize: '0.875rem',
    textTransform: 'none',
    alignItems: 'flex-end',
  },
}));

export const PracticeOverlay = ({ practice, learningLabSiteCopy, onClose }) => {
  const [isComplete, setIsComplete] = useState(false);
  const [slideWith, setSlideWidth] = useState();
  const resourceProgressRef = useRef(0);
  const isPausedRef = useRef(false);

  const ratings = useSelector(state => state.resources.helpfulRatings);
  const asset = _get(practice, 'asset');
  const contentType = _get(asset, 'fields.file.contentType');
  const type = contentType
    ? typesRegex.find(item => item.regex.test(contentType))?.type
    : practice.videoUrl && practiceTypesMapping.video;

  const { saved, handleSave } = useSaveToFavorite({
    item: { ...practice, id: practice.id },
    type: 'practice',
    trackingProps: {
      methods: practice.methods,
    },
  });
  const classes = useStyles({ type, saved });

  if (!type) {
    return null;
  }

  const onComplete = () => {
    setIsComplete(true);
    Mixpanel.track('Practice Card - Completed', {
      name: practice.name,
      methods: practice.methods,
    });
  };

  const onEnded = () => {
    onComplete();
  };

  const handleClose = e => {
    e.stopPropagation();
    Mixpanel.track('Practice Card - Close', {
      name: practice.name,
      resourceProgress: `${resourceProgressRef.current}%`,
      methods: practice.methods,
    });
    onClose();
  };

  const handlePause = e => {
    const percentage = Math.round(
      (e.target.currentTime / e.target.duration) * 100,
    );
    resourceProgressRef.current = percentage;
    isPausedRef.current = true;
    Mixpanel.track('Practice Card - Pause', {
      name: practice.name,
      methods: practice.methods,
      resourceProgress: `${resourceProgressRef.current}%`,
    });
  };

  const handleResume = () => {
    if (isPausedRef.current) {
      Mixpanel.track('Practice Card - Resume', {
        name: practice.name,
        methods: practice.methods,
      });
    }
    isPausedRef.current = false;
  };

  const onSlideChange = (current, next) => {
    const eventName =
      next > current
        ? 'Practice Card - Next Clicked'
        : ' Practice Card - Previous Clicked';

    Mixpanel.track(eventName, {
      name: practice.name,
      slide: current + 1,
    });
  };

  const onStartAgain = () => {
    setIsComplete(false);
    Mixpanel.track('Practice Card - Restart', {
      name: practice.name,
      methods: practice.methods,
    });
  };

  const refCallback = node => {
    if (node && type === practiceTypesMapping.slides) {
      setSlideWidth(node.offsetWidth - 48);
    }
  };

  const audioElement = (
    <PracticeAudio
      onPlay={handleResume}
      onPause={handlePause}
      onEnded={onEnded}
      asset={asset}
      practice={practice}
    />
  );
  const mapTypeToContent = {
    [practiceTypesMapping.video]: (
      <PracticeVideo
        onPlay={handleResume}
        onPause={handlePause}
        onEnded={onEnded}
        practice={practice}
        autoplay
      />
    ),
    [practiceTypesMapping.audio]: audioElement,
    [practiceTypesMapping.slides]: (
      <PracticeSlides
        asset={asset}
        pageWidth={slideWith}
        onComplete={onComplete}
        learningLabSiteCopy={learningLabSiteCopy}
        onChange={onSlideChange}
      />
    ),
  };

  const onTouchStart = e => {
    e.stopPropagation();
  };

  return (
    <Dialog
      open
      classes={{ paper: classes.paper }}
      onClose={handleClose}
      onTouchStart={onTouchStart}
    >
      <div className={classes.header}>
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={handleClose}
        >
          <CloseIcon />
        </IconButton>
        <div className={classes.titleContainer}>
          <Typography variant="h5" color="textPrimary">
            {_get(practice, 'title')}
          </Typography>
          <IconButton
            className={classes.heartButton}
            onClick={handleSave}
            aria-label="Favorite"
          >
            <HeartIcon fill={saved ? 'currentColor' : 'none'} />
          </IconButton>
        </div>
      </div>
      <DialogContent ref={refCallback} className={classes.content}>
        {isComplete ? (
          <ResourcePageHelpfulContent
            customClasses={{
              root: classes.resourceHelpful,
              btnWrapper: classes.resourceHelpfulBtnWrapper,
            }}
            type="practice"
            helpfulLocation="practice"
            resourceID={practice.id}
            ratings={ratings}
            item={{ fields: practice, sys: { id: practice.id } }}
          />
        ) : (
          mapTypeToContent[type]
        )}
      </DialogContent>
      {isComplete && (
        <DialogActions>
          <Button
            className={classes.startAgainButton}
            size="small"
            startIcon={<ReplayIcon fontSize="default" />}
            onClick={onStartAgain}
            variant="text"
          >
            {_get(learningLabSiteCopy, 'pageCopy.startPracticeAgain')}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};
